import type { stacks } from "@dropbox/api-v2-client";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashCardType } from "@mirage/analytics/events/enums/dash_card_type";
import { PAP_Collapse_DashCard } from "@mirage/analytics/events/types/collapse_dash_card";
import { PAP_Expand_DashCard } from "@mirage/analytics/events/types/expand_dash_card";
import { useCollapsedCardSettings } from "@mirage/service-settings/useCollapsedCardSettings";
import { useStackItemCollapseDescriptionSettings } from "@mirage/service-settings/useStackItemByIdCollapseDescriptionSettings";
import type { StackItemShortcutWithMetadata } from "@mirage/service-stacks/service/types";
import { DEFAULT_SECTION_ID } from "@mirage/service-stacks/service/utils";
import { activeStackHasWritePermissionsAtom } from "@mirage/stacks/atoms";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import type { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { featureLine } from "../../constants";
import { DroppableEmptySection } from "../../DragAndDrop/DroppableEmptySection";
import { WithDragIconHandle } from "../../DragAndDrop/WithDragIconHandle";
import { Header } from "./Header";
import styles from "./Section.module.css";
import { SectionItemsTable } from "./SectionItemsTable";

const dashCardType: DashCardType = "stack_section";

export const Section = ({
  section,
  items,
  dragHandleProps,
}: {
  section: stacks.Section;
  items: StackItemShortcutWithMetadata[];
  dragHandleProps?: DraggableProvidedDragHandleProps;
}) => {
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { isReady } = useStackItemCollapseDescriptionSettings();
  const { isCollapsed, setCollapsed } = useCollapsedCardSettings(
    `stack_section:${section.id}`,
  );

  const sectionId = section.id ?? DEFAULT_SECTION_ID;
  const isDefaultSection = sectionId === DEFAULT_SECTION_ID;
  const isEmpty = items.length === 0;
  const showHeader = !isDefaultSection;

  const handleToggleCollapsed = () => {
    const papEvent = isCollapsed ? PAP_Expand_DashCard : PAP_Collapse_DashCard;
    reportPapEvent(papEvent({ featureLine, dashCardType }));
    setCollapsed(!isCollapsed);
  };

  // If we haven't yet determined if the user has set a collapsed state yet,
  // then return nothing until we do, so that we don't render a card that
  // flickers from expanded to collapsed
  if (isCollapsed === undefined) {
    return null;
  }

  const renderHeader = () => {
    const headerContent = (
      <Header
        section={section}
        linkCount={items.length}
        isCollapsed={isCollapsed}
        setIsCollapsed={handleToggleCollapsed}
      />
    );

    return hasWritePermissions && dragHandleProps ? (
      <div {...dragHandleProps} className={styles.dragHandle}>
        <WithDragIconHandle>{headerContent}</WithDragIconHandle>
      </div>
    ) : (
      headerContent
    );
  };

  if (!isReady || (isEmpty && isDefaultSection)) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.sectionContainer,
        isCollapsed && styles.collapsed,
        isEmpty && styles.empty,
        isEmpty && styles.emptySectionContainer,
      )}
    >
      {showHeader && <div className={styles.header}>{renderHeader()}</div>}

      {!isCollapsed && (
        <div className={styles.content}>
          {isEmpty ? (
            <DroppableEmptySection
              sectionId={sectionId}
              mustCreateNewSection={false}
              isHoveringForMoment={false}
              onDragEnterMoment={() => {}}
            />
          ) : (
            <SectionItemsTable items={items} />
          )}
        </div>
      )}
    </div>
  );
};
